// src/pages/Evenements.js
import React, { useState, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet";
import axios from "axios";
import BASE_URL from "../config";
import EventCountdown from "../components/EventCountdown";
import {
  CaciStyles, PageHeader, SectionNote, Seal,
  useReveal, useScrollProgress, langPath, truncate,
} from "../components/CaciUI";

/* ═══════════════════════════════════════════════════════════════════════════
   EVENEMENTS
   ───────────────────────────────────────────────────────────────────────────
   The behaviour here was already carefully thought through and is preserved
   exactly: the page always opens on "À venir" and never switches filter by
   itself; `can_register` comes from the API and uses the same rule the submit
   endpoint enforces, so a card can never invite someone into a form that will
   be refused; the phase wording on a closed event is unchanged. The
   EventCountdown component is untouched.

   What changed is the surface, plus:
   · A failed request set the list to an empty array, so a broken endpoint was
     indistinguishable from "no events". Failure is now its own state, with a
     retry.
   · Loading showed a bare spinner; skeletons hold the card layout instead.
   · The filter counts are computed once per event list rather than by walking
     the array four times on every render.
   ═══════════════════════════════════════════════════════════════════════════ */

const FILTERS = ["upcoming", "ongoing", "past", "all"];

function Evenements() {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const isRtl = lang === "ar";

  const [events, setEvents] = useState([]);
  const [state, setState] = useState("loading"); // loading | ready | error

  /* Opens on "À venir" — including when nothing is upcoming, in which case the
     filter shows its own empty state and offers the full list. It must only
     ever change because the visitor clicked. */
  const [filter, setFilter] = useState("upcoming");
  const [catFilter, setCatFilter] = useState("all");

  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    let active = true;
    setState("loading");
    (async () => {
      try {
        const res = await axios.get(`${BASE_URL}/api/events-public`, {
          params: { scope: "all" },
          headers: { "Accept-Language": lang },
        });
        if (!active) return;
        setEvents(Array.isArray(res.data) ? res.data : []);
        setState("ready");
      } catch (e) {
        if (!active) return;
        /* Previously this set events to [] and the page showed "no events" —
           telling the visitor the chamber has nothing planned, rather than
           that the request failed. */
        console.error("Evenements: could not load the event list —", e);
        setState("error");
      }
    })();
    return () => { active = false; };
  }, [lang, reloadKey]);

  const pageRef = useReveal([events, state, filter, catFilter]);
  const progress = useScrollProgress();

  const formatDate = (iso) => {
    if (!iso) return "";
    const locale = { en: "en-US", ar: "ar-DZ", fr: "fr-FR" }[lang] || "fr-FR";
    return new Date(iso).toLocaleDateString(locale, {
      year: "numeric", month: "long", day: "numeric", hour: "2-digit", minute: "2-digit",
    });
  };

  const filtered = events.filter((e) =>
    (filter === "all" || e.status === filter) &&
    (catFilter === "all" || String(e.category_id) === String(catFilter))
  );

  const categories = useMemo(() => Array.from(
    events.reduce((map, e) => {
      if (e.category_id && e.category) map.set(String(e.category_id), e.category);
      return map;
    }, new Map())
  ).map(([id, label]) => ({ id, label })), [events]);

  /* One pass instead of four array walks per render. */
  const counts = useMemo(() => {
    const c = { all: events.length, upcoming: 0, ongoing: 0, past: 0 };
    events.forEach((e) => { if (c[e.status] !== undefined) c[e.status] += 1; });
    return c;
  }, [events]);

  const filterLabel = {
    upcoming: t("events.upcoming", "À venir"),
    ongoing: t("events.ongoing", "En cours"),
    past: t("events.past", "Terminé"),
    all: t("events.all", "Tous"),
  };

  const registerHref = (event) =>
    lang === "fr" ? event.register_url : `/${lang}${event.register_url}`;

  const title = t("events.title", "Événements");

  return (
    <div className={`caci-page${isRtl ? " is-rtl" : ""}`} ref={pageRef} dir={isRtl ? "rtl" : "ltr"}>
      <CaciStyles />
      <style>{PAGE_CSS}</style>

      <Helmet>
        <title>{title} — {t("menu.caci", "CACI")}</title>
        <meta name="description" content={t("events.meta", "Découvrez et inscrivez-vous aux événements de la CACI.")} />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={t("events.meta", "")} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={typeof window !== "undefined" ? window.location.href : ""} />
      </Helmet>

      <div
        className="caci-progress"
        style={{ transform: `scaleX(${progress})`, opacity: progress > 0.005 ? 1 : 0 }}
        aria-hidden="true"
      />

      <PageHeader
        title={title}
        sub={t("events.meta", "")}
        crumbs={[{ label: t("menu.home", "Accueil"), to: langPath(lang, "/") }, { label: title }]}
        count={
          state === "ready" && events.length > 0
            ? { value: counts.all, label: title }
            : null
        }
      />

      {/* ══════════════════ FILTERS ══════════════════ */}
      {state === "ready" && events.length > 0 && (
        <div className="bar">
          <div className="caci-shell">
            <div className="evt-filters" role="tablist" aria-label={title}>
              {FILTERS.map((key) => (
                <button
                  key={key}
                  type="button"
                  role="tab"
                  aria-selected={filter === key}
                  className={`evt-chip${filter === key ? " is-active" : ""}`}
                  onClick={() => setFilter(key)}
                >
                  {filterLabel[key]}
                  <span className="evt-chip-n">{counts[key]}</span>
                </button>
              ))}
            </div>

            {categories.length > 0 && (
              <div className="evt-cats">
                <div className="select">
                  <select
                    value={catFilter}
                    onChange={(e) => setCatFilter(e.target.value)}
                    aria-label={t("menu.Categories", "Catégories")}
                  >
                    <option value="all">{t("events.all_categories", "Toutes les catégories")}</option>
                    {categories.map((c) => (
                      <option key={c.id} value={c.id}>{c.label}</option>
                    ))}
                  </select>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      <section className="caci-section">
        <div className="caci-shell">
          {state === "loading" && (
            <div className="caci-grid" aria-hidden="true">
              {Array.from({ length: 6 }, (_, i) => (
                <div key={i} className="skel-card">
                  <span className="skel skel-media" />
                  <div className="skel-card-body">
                    <span className="skel" style={{ height: 13, width: "86%", borderRadius: 7 }} />
                    <span className="skel" style={{ height: 13, width: "60%", borderRadius: 7 }} />
                    <span className="skel" style={{ height: 40, width: "100%", borderRadius: 12, marginBlockStart: 8 }} />
                  </div>
                </div>
              ))}
            </div>
          )}

          {state === "error" && (
            <SectionNote
              state="error"
              title={t("search.errorTitle", "Chargement impossible")}
              text={t("home.section_error", "La liste des événements n'a pas pu être chargée.")}
              action={
                <button type="button" className="btn-quiet" onClick={() => setReloadKey((k) => k + 1)}>
                  <i className="bi bi-arrow-clockwise" />
                  {t("home.retry", "Réessayer")}
                </button>
              }
            />
          )}

          {state === "ready" && events.length === 0 && (
            <SectionNote
              title={t("events.none", "Aucun événement pour le moment")}
              text={t("events.none_text", "")}
            />
          )}

          {state === "ready" && events.length > 0 && filtered.length === 0 && (
            <SectionNote
              title={t("events.none_in_filter", "Rien dans cette sélection")}
              text={t("events.none_in_filter_text", "")}
              action={
                <button
                  type="button" className="btn-quiet"
                  onClick={() => { setFilter("all"); setCatFilter("all"); }}
                >
                  <i className="bi bi-arrow-counterclockwise" />
                  {t("events.all", "Tous")}
                </button>
              }
            />
          )}

          {state === "ready" && filtered.length > 0 && (
            <div className="caci-grid">
              {filtered.map((event, i) => {
                const closed = event.can_register === false;
                const past = event.status === "past";
                return (
                  <div key={event.id} data-rv="rise" style={{ "--i": i % 6 }}>
                    <article className={`evt-card${past ? " is-past" : ""}`}>
                      <div className="evt-media">
                        {event.image ? (
                          <img
                            src={`${BASE_URL}${event.image}`}
                            alt="" loading="lazy" decoding="async"
                            onError={(e) => {
                              e.currentTarget.style.display = "none";
                              e.currentTarget.parentElement.classList.add("is-empty");
                            }}
                          />
                        ) : (
                          <span className="evt-media-ph"><i className="bi bi-calendar-event" /></span>
                        )}
                        <div className="evt-badges">
                          <span className={`evt-badge is-${event.status || "upcoming"}`}>
                            {filterLabel[event.status] || filterLabel.upcoming}
                          </span>
                          {event.is_paid ? (
                            <span className="evt-badge is-paid">
                              <i className="bi bi-cash-coin" /> {event.prix} DZD
                            </span>
                          ) : (
                            <span className="evt-badge is-free">
                              <i className="bi bi-gift" /> {t("events.free", "Gratuit")}
                            </span>
                          )}
                        </div>
                      </div>

                      <div className="evt-body">
                        <h2 className="evt-title">
                          <Link to={registerHref(event)} dir="auto">{event.title}</Link>
                        </h2>

                        <ul className="evt-meta">
                          <li><i className="bi bi-calendar3" /><span>{formatDate(event.date_debut)}</span></li>
                          {event.lieu && (
                            <li>
                              <i className="bi bi-geo-alt" />
                              <span dir="auto">{event.lieu}{event.ville ? `, ${event.ville}` : ""}</span>
                            </li>
                          )}
                          {event.category && (
                            <li><i className="bi bi-tag" /><span dir="auto">{event.category}</span></li>
                          )}
                        </ul>

                        {event.description && (
                          <p className="evt-desc" dir="auto">{truncate(event.description, 150)}</p>
                        )}

                        {/* The wrapper used to be className="evt-countdown",
                            the same class EventCountdown puts on its own root
                            element — so the mint panel below was applied twice,
                            a padded box inside a padded box. Renamed. */}
                        {!past && (
                          <div className="evt-cd-box">
                            <span className="evt-cd-caption">
                              <Seal size={12} />
                              {event.status === "ongoing"
                                ? t("events.happening_now", "C'est en cours")
                                : t("events.starts_in", "Commence dans")}
                            </span>
                            <EventCountdown date={event.date_debut} dateEnd={event.date_fin} size="sm" />
                          </div>
                        )}

                        <div className="evt-foot">
                          {/* can_register comes from the API and mirrors the
                              rule the submit endpoint enforces, so a card can
                              never invite someone into a form that will be
                              refused. */}
                          <Link
                            to={registerHref(event)}
                            className={`evt-cta${closed || past ? " is-quiet" : ""}`}
                          >
                            {closed || past
                              ? t("events.view", "Voir")
                              : t("events.register", "S'inscrire")}
                            <i className={`bi bi-arrow-${isRtl ? "left" : "right"}`} />
                          </Link>

                          {closed && (
                            <span className="evt-closed">
                              <i className="bi bi-lock" aria-hidden="true" />
                              {event.phase === "finished"
                                ? t("events.finished", "Terminé")
                                : event.phase === "running"
                                ? t("events.ongoing", "En cours")
                                : event.phase === "closing"
                                ? t("events.deadline_passed", "Date limite dépassée")
                                : t("events.closed", "Inscriptions closes")}
                            </span>
                          )}
                        </div>
                      </div>
                    </article>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}

const PAGE_CSS = `
.caci-page .evt-filters {
  display: flex; gap: 8px; overflow-x: auto;
  scroll-snap-type: x proximity; scrollbar-width: none;
  margin-inline: calc(var(--gut) * -1); padding-inline: var(--gut);
  padding-block: 2px;
}
.caci-page .evt-filters::-webkit-scrollbar { display: none; }
.caci-page .evt-chip {
  flex: 0 0 auto; scroll-snap-align: start;
  display: inline-flex; align-items: center; gap: 9px;
  min-height: 48px; padding: 0 18px; border-radius: 999px; cursor: pointer;
  border: 1px solid var(--rule); background: var(--surface); color: var(--muted);
  font-family: var(--body); font-size: 14px; font-weight: 600; white-space: nowrap;
  transition: background var(--t1) var(--e), color var(--t1) var(--e),
              border-color var(--t1) var(--e), transform var(--t1) var(--e);
}
.caci-page .evt-chip:hover { border-color: var(--rule-2); color: var(--pine); }
.caci-page .evt-chip.is-active {
  background: var(--pine); border-color: var(--pine); color: #fff;
  box-shadow: 0 12px 26px -14px rgba(15,74,48,.9);
}
.caci-page .evt-chip:active { transform: scale(.97); }
.caci-page .evt-chip-n {
  min-width: 24px; height: 22px; padding-inline: 7px; border-radius: 999px;
  display: inline-grid; place-items: center;
  background: var(--mint); color: var(--pine);
  font-family: var(--mono); font-size: 11px; font-weight: 700;
  font-variant-numeric: tabular-nums;
}
.caci-page .evt-chip.is-active .evt-chip-n { background: rgba(255,255,255,.18); color: #fff; }
.caci-page .evt-cats { margin-block-start: 10px; }
@media (min-width: 768px) {
  .caci-page .evt-cats { max-width: 320px; }
}

.caci-page .evt-card {
  position: relative; display: flex; flex-direction: column; height: 100%;
  border-radius: 18px; overflow: hidden;
  background: var(--surface); border: 1px solid var(--rule);
  box-shadow: 0 4px 18px -12px rgba(8,21,14,.3);
  transition: transform var(--t2) var(--e), box-shadow var(--t2) var(--e),
              border-color var(--t1) var(--e);
}
.caci-page .evt-card:hover {
  transform: translateY(-8px); border-color: rgba(176,138,46,.42);
  box-shadow: 0 38px 64px -34px rgba(15,74,48,.45);
}
/* A finished event stays legible but visibly settled. */
.caci-page .evt-card.is-past .evt-media img { filter: grayscale(.75); opacity: .78; }
.caci-page .evt-media {
  position: relative; height: 178px; flex: 0 0 auto; overflow: hidden;
  background: linear-gradient(150deg, var(--mint), var(--paper));
  display: grid; place-items: center;
}
.caci-page .evt-media img {
  position: absolute; inset: 0; width: 100%; height: 100%; object-fit: cover;
  transition: transform 1s var(--e), filter var(--t2) var(--e);
}
.caci-page .evt-card:hover .evt-media img { transform: scale(1.06); }
.caci-page .evt-media-ph { color: var(--rule-2); font-size: 40px; }
.caci-page .evt-badges {
  position: absolute; z-index: 2; inset-block-start: 12px;
  inset-inline: 12px; display: flex; flex-wrap: wrap; gap: 6px;
}
.caci-page .evt-badge {
  display: inline-flex; align-items: center; gap: 6px;
  min-height: 28px; padding: 0 11px; border-radius: 999px;
  background: rgba(255,255,255,.94); color: var(--pine);
  -webkit-backdrop-filter: blur(8px); backdrop-filter: blur(8px);
  box-shadow: 0 4px 14px -6px rgba(8,21,14,.5);
  font-family: var(--mono); font-size: 10px; font-weight: 700;
  letter-spacing: .1em; text-transform: uppercase;
}
.caci-page .evt-badge.is-upcoming { color: var(--pine); }
.caci-page .evt-badge.is-ongoing  { background: var(--leaf); color: #fff; }
.caci-page .evt-badge.is-past     { background: rgba(8,21,14,.72); color: rgba(255,255,255,.85); }
.caci-page .evt-badge.is-paid     { color: #7A5A12; }
.caci-page .evt-badge.is-free     { color: #14544C; }

.caci-page .evt-body { flex: 1; display: flex; flex-direction: column; padding: 16px 20px 20px; }
.caci-page .evt-title { margin: 0 0 12px; }
.caci-page .evt-title a {
  font-family: var(--display); font-size: 16.5px; font-weight: 700;
  letter-spacing: -.02em; line-height: 1.32; color: var(--ink);
  text-decoration: none; display: -webkit-box;
  -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden;
  transition: color var(--t1) var(--e);
}
.caci-page .evt-card:hover .evt-title a { color: var(--pine); }
.caci-page .evt-meta { list-style: none; margin: 0 0 12px; padding: 0; display: grid; gap: 7px; }
.caci-page .evt-meta li {
  display: flex; align-items: flex-start; gap: 9px;
  font-size: 13px; line-height: 1.45; color: var(--muted);
}
.caci-page .evt-meta i { flex: 0 0 auto; margin-block-start: 2px; color: var(--brass); font-size: 12px; }
.caci-page .evt-desc {
  margin: 0 0 14px; font-size: 13.5px; line-height: 1.6; color: var(--muted);
  display: -webkit-box; -webkit-line-clamp: 3; -webkit-box-orient: vertical; overflow: hidden;
}
.caci-page .evt-cd-box {
  margin-block-end: 16px; padding: 12px 14px; border-radius: 14px;
  background: var(--mint);
}
.caci-page .evt-cd-caption {
  display: flex; align-items: center; gap: 8px; margin-block-end: 8px;
  font-family: var(--mono); font-size: 10px; font-weight: 700;
  letter-spacing: .12em; text-transform: uppercase; color: var(--pine);
}
.caci-page .evt-cd-caption .seal { color: var(--brass); }

/* ── The countdown itself ──────────────────────────────────────────────────
   This is the other half of why the countdown looked broken here. Every rule
   that gives it a shape — .evt-countdown's display:flex, .evt-cd-unit,
   .evt-cd-value, .evt-cd-label, .evt-cd-sep, the live/ended pills and the
   pulsing dot — lives in HomeEventsSection.js, scoped to .hes-section.
   This page renders inside .caci-page and never inside .hes-section, so none
   of it applied: the four units fell down the card as bare block-level divs
   with unstyled numbers. Restated here in the page's own tokens.

   NOTE: no backticks in here. This whole block sits inside the PAGE_CSS
   template literal, so a backtick closes the string and the CSS after it is
   parsed as JavaScript. */
.caci-page .evt-countdown {
  display: flex; align-items: flex-start; gap: 8px;
}
.caci-page .evt-cd-unit {
  display: flex; flex-direction: column; align-items: center;
}
.caci-page .evt-cd-value {
  min-width: 34px; padding: 4px 7px; border-radius: 8px;
  background: var(--surface); border: 1px solid var(--rule);
  font-family: var(--mono); font-size: 14.5px; font-weight: 700;
  font-variant-numeric: tabular-nums; line-height: 1.2;
  text-align: center; color: var(--pine);
}
.caci-page .evt-cd-label {
  margin-block-start: 4px;
  font-family: var(--mono); font-size: 9px; font-weight: 600;
  letter-spacing: .1em; text-transform: uppercase; color: var(--muted);
}
.caci-page .evt-cd-sep {
  margin-block-start: 4px;
  font-family: var(--mono); font-size: 13px; font-weight: 700;
  color: var(--rule-2);
}
.caci-page .evt-countdown-lg { gap: 12px; }
.caci-page .evt-countdown-lg .evt-cd-value { min-width: 52px; padding: 8px 6px; font-size: 26px; }
.caci-page .evt-countdown-lg .evt-cd-label { font-size: 10px; }
.caci-page .evt-countdown-lg .evt-cd-sep { font-size: 22px; }

.caci-page .evt-countdown-live,
.caci-page .evt-countdown-ended {
  display: inline-flex; align-items: center; gap: 7px;
  padding: 6px 14px; border-radius: 999px;
  font-family: var(--mono); font-size: 11px; font-weight: 700;
  letter-spacing: .06em; text-transform: uppercase;
}
.caci-page .evt-countdown-live { background: var(--surface); color: var(--leaf); }
.caci-page .evt-countdown-ended { background: var(--surface); color: var(--muted); }
.caci-page .evt-live-dot {
  width: 7px; height: 7px; border-radius: 50%;
  background: var(--leaf); animation: evtPulse 1.2s ease-in-out infinite;
}
@keyframes evtPulse {
  0%, 100% { opacity: 1; transform: scale(1); }
  50%      { opacity: .35; transform: scale(.8); }
}
@media (prefers-reduced-motion: reduce) {
  .caci-page .evt-live-dot { animation: none; }
}

.caci-page .evt-foot {
  display: flex; flex-wrap: wrap; align-items: center; gap: 10px;
  margin-top: auto; padding-top: 4px;
}
.caci-page .evt-cta {
  flex: 1 1 auto;
  display: inline-flex; align-items: center; justify-content: center; gap: 9px;
  min-height: 48px; padding: 0 22px; border-radius: 999px;
  background: linear-gradient(180deg, var(--leaf-2), var(--leaf)); color: #fff;
  text-decoration: none; font-size: 14.5px; font-weight: 600;
  box-shadow: 0 14px 30px -16px rgba(26,122,74,.9);
  transition: transform var(--t1) var(--e), background var(--t1) var(--e);
}
.caci-page .evt-cta i { transition: transform var(--t1) var(--e); }
.caci-page .evt-cta:hover { color: #fff; transform: translateY(-2px); }
.caci-page .evt-cta:hover i { transform: translateX(4px); }
.caci-page.is-rtl .evt-cta:hover i { transform: translateX(-4px); }
.caci-page .evt-cta:active { transform: translateY(0) scale(.98); }
/* Registration closed: still reachable, but no longer an invitation. */
.caci-page .evt-cta.is-quiet {
  background: transparent; color: var(--pine);
  border: 1px solid var(--rule-2); box-shadow: none;
}
.caci-page .evt-cta.is-quiet:hover { background: var(--mint); color: var(--pine); }
.caci-page .evt-closed {
  display: inline-flex; align-items: center; gap: 7px;
  font-family: var(--mono); font-size: 10.5px; font-weight: 600;
  letter-spacing: .06em; color: var(--muted);
}
.caci-page .evt-closed i { color: var(--brass); }
`;

export default Evenements;
